import React, { forwardRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Formik } from 'formik';
// @ts-ignore
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import useAppointment from 'src/hooks/useAppointment';
import AlertP from 'src/components/Alert';
import { useParams } from 'react-router-dom';
import { DateTimePicker } from '@material-ui/pickers';
import useLead from 'src/hooks/useLead';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
    Card,
    FormHelperText,
    CardContent,
    CardActions,
    Typography,
    TextField,
    Box,
    Button,
    Divider,
    colors,
    Theme,
} from '@material-ui/core';
import useStore from 'src/hooks/useStore';
import { IAddEditEvent, TUpdateLeadBody } from './types';
import { IStore } from 'src/interfaces/store';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        width: 700,
        maxHeight: '100%',
        overflowY: 'auto',
        maxWidth: '100%',
    },
    field: {
        marginTop: theme.spacing(3),
    },
    confirmButton: {
        color: theme.palette.common.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900],
        },
        marginLeft: theme.spacing(2),
    },
    cancelButton: {
        color: theme.palette.common.white,
        backgroundColor: colors.red[600],
        '&:hover': {
            backgroundColor: colors.red[900],
        },
        marginLeft: theme.spacing(2),
    },
}));

export const AddEditEvent = forwardRef<HTMLDivElement, IAddEditEvent>(
    (props, ref) => {
        const { event, onDelete, onCancel, onAdd, onEdit, className, ...rest } =
            props;

        const classes = useStyles();
        const { enqueueSnackbar } = useSnackbar();
        const { createAppointment, error, appointment } = useAppointment();
        const route = useParams<{ id: string }>();
        const { lead, updateLead, addAppointment } = useLead();
        const { t } = useTranslation();
        const [submitedForm, setSubmitedForm] = useState(false);
        const { stores } = useStore();

        useEffect(() => {
            if (submitedForm) {
                if (!error) {
                    enqueueSnackbar(t('SnackBar.AppointmentCreated'), {
                        variant: 'success',
                    });
                }
                setSubmitedForm(false);
                onAdd();
            }

            // eslint-disable-next-line
        }, [submitedForm]);

        return (
            <Card {...rest} className={clsx(classes.root, className)} ref={ref}>
                <Formik
                    initialValues={{
                        title: '',
                        description: '',
                        startDate: moment().endOf('hour').add('1', 'minute'),
                        endDate: '',
                        lead: route.id,
                        email: lead.email,
                        customer: lead.name,
                        store: '',
                        duration: 1,
                        action: '',
                        submit: '',
                    }}
                    validationSchema={Yup.object().shape({
                        title: Yup.string().max(255),
                        description: Yup.string().max(255),
                        startDate: Yup.date().min(
                            new Date(),
                            t?.('Yup.TodayA') || 'Must be after today'
                        ),
                        endDate: Yup.date().when(
                            'startDate',
                            (start: any, schema: Yup.DateSchema) =>
                                start &&
                                schema.min(
                                    start,
                                    t?.('Forms.ErrorDate') ||
                                        'End date must be after start date'
                                )
                        ),
                    })}
                    onSubmit={async (
                        values,
                        { resetForm, setErrors, setStatus, setSubmitting }
                    ) => {
                        try {
                            if (
                                parseInt(moment(values.startDate).format('H')) <
                                    8 ||
                                parseInt(moment(values.startDate).format('H')) >
                                    21
                            ) {
                                setErrors({
                                    submit: t('Errors.HorarioLaboral'),
                                });
                            } else if (values.description === '') {
                                setErrors({ submit: t('Comments.NoComment') });
                            } else if (lead && lead.agent && lead.agent._id) {
                                values.endDate = moment(values.startDate)
                                    .add(values.duration, 'hours')
                                    .format();

                                let fechaString = moment(
                                    values.startDate
                                ).format('DD MMMM YYYY HH:mm a');

                                fechaString = fechaString.replace(
                                    'January',
                                    'Enero'
                                );
                                fechaString = fechaString.replace(
                                    'February',
                                    'febrero'
                                );
                                fechaString = fechaString.replace(
                                    'March',
                                    'Marzo'
                                );
                                fechaString = fechaString.replace(
                                    'April',
                                    'Abril'
                                );
                                fechaString = fechaString.replace(
                                    'May',
                                    'Mayo'
                                );
                                fechaString = fechaString.replace(
                                    'June',
                                    'Junio'
                                );
                                fechaString = fechaString.replace(
                                    'July',
                                    'Julio'
                                );
                                fechaString = fechaString.replace(
                                    'August',
                                    'Agosto'
                                );
                                fechaString = fechaString.replace(
                                    'September',
                                    'Septiembre'
                                );
                                fechaString = fechaString.replace(
                                    'October',
                                    'Octubre'
                                );
                                fechaString = fechaString.replace(
                                    'November',
                                    'Noviembre'
                                );
                                fechaString = fechaString.replace(
                                    'December',
                                    'Diciembre'
                                );

                                await createAppointment({
                                    ...values,
                                    fechaString,
                                    substatus: '605bd6b0bed49524ae40f885',
                                    user: lead.agent._id,
                                });

                                addAppointment(appointment);

                                let updateLeadBody: TUpdateLeadBody = {
                                    status: '604f80222b372e0cb11966dc',
                                    substatus: '605bd6b0bed49524ae40f885',
                                    isContacted: true,
                                };

                                if (!lead.firstTask)
                                    updateLeadBody.firstTask = new Date();

                                updateLead(updateLeadBody, lead._id);

                                setSubmitedForm(true);
                                resetForm();
                            } else {
                                enqueueSnackbar(t('Yup.NoAgent'), {
                                    variant: 'error',
                                });
                            }
                        } catch (err) {
                            setStatus({ success: false });
                            if (err instanceof Error)
                                setErrors({ submit: err.message });
                            setSubmitting(false);
                        }
                    }}
                >
                    {({
                        errors,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        isSubmitting,
                        setFieldTouched,
                        setFieldValue,
                        touched,
                        values,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <CardContent>
                                <Box p={2}>
                                    <Box p={1}>
                                        <Typography
                                            align="center"
                                            gutterBottom
                                            variant="h3"
                                            color="textPrimary"
                                        >
                                            {t('Buttons.Create')}{' '}
                                            {t('Leads.Appointment')}
                                        </Typography>
                                    </Box>
                                    <Box mt={1}>
                                        <TextField
                                            error={Boolean(
                                                touched.title && errors.title
                                            )}
                                            helperText={
                                                touched.title && errors.title
                                            }
                                            onBlur={handleBlur}
                                            className={classes.field}
                                            fullWidth
                                            label={t('Forms.Title')}
                                            name="title"
                                            required
                                            onChange={handleChange}
                                            value={values.title}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box mt={1}>
                                        <TextField
                                            error={Boolean(
                                                touched.description &&
                                                    errors.description
                                            )}
                                            helperText={
                                                touched.description &&
                                                errors.description
                                            }
                                            onBlur={handleBlur}
                                            className={classes.field}
                                            fullWidth
                                            required
                                            label={t('Forms.Description')}
                                            name="description"
                                            onChange={handleChange}
                                            value={values.description}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box mt={4}>
                                        <TextField
                                            error={Boolean(
                                                touched.action && errors.action
                                            )}
                                            fullWidth
                                            helperText={
                                                touched.action && errors.action
                                            }
                                            label={t('Forms.Action')}
                                            name="action"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            select
                                            required
                                            variant="outlined"
                                            SelectProps={{ native: true }}
                                        >
                                            <option key={0} value={''}></option>
                                            <option
                                                key={1}
                                                value={'information'}
                                            >
                                                {t('Forms.Information')}
                                            </option>
                                            <option
                                                key={2}
                                                value={'documentation'}
                                            >
                                                {t('Forms.Documentation')}
                                            </option>
                                            <option key={3} value={'drive'}>
                                                {t('Forms.Drive')}
                                            </option>
                                            <option key={4} value={'delivery'}>
                                                {t('Forms.Delivery')}
                                            </option>
                                        </TextField>
                                    </Box>
                                    <Box mt={4}>
                                        <DateTimePicker
                                            error={Boolean(
                                                touched.startDate &&
                                                    errors.startDate
                                            )}
                                            onBlur={handleBlur}
                                            helperText={
                                                touched?.startDate &&
                                                // @ts-ignore
                                                errors?.startDate?.toString()
                                            }
                                            fullWidth
                                            minutesStep={10}
                                            disablePast
                                            required
                                            inputVariant="outlined"
                                            label={t('Forms.StartDateApp')}
                                            name="startDate"
                                            onClick={() =>
                                                setFieldTouched('end')
                                            }
                                            onChange={(date) => {
                                                setFieldValue(
                                                    'startDate',
                                                    date
                                                );
                                            }}
                                            value={values.startDate}
                                        />
                                    </Box>
                                    <Box mt={4}>
                                        <TextField
                                            error={Boolean(
                                                touched.duration &&
                                                    errors.duration
                                            )}
                                            fullWidth
                                            helperText={
                                                touched.duration &&
                                                errors.duration
                                            }
                                            label={t('Forms.AppDuration')}
                                            name="duration"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            select
                                            required
                                            variant="outlined"
                                            SelectProps={{ native: true }}
                                        >
                                            <option key={0} value={1}>
                                                {t('Appointments.App1h')}
                                            </option>
                                            <option key={1} value={2}>
                                                {t('Appointments.App2h')}
                                            </option>
                                        </TextField>
                                        <Box mt={4}>
                                            <TextField
                                                error={Boolean(
                                                    touched.store &&
                                                        errors.store
                                                )}
                                                fullWidth
                                                helperText={
                                                    touched.store &&
                                                    errors.store
                                                }
                                                label={t('Forms.Store')}
                                                name="store"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                select
                                                required
                                                variant="outlined"
                                                SelectProps={{ native: true }}
                                            >
                                                <option
                                                    key={0}
                                                    value={''}
                                                ></option>
                                                {stores &&
                                                    stores.map(
                                                        (store: IStore) => (
                                                            <option
                                                                key={store._id}
                                                                value={
                                                                    store._id
                                                                }
                                                                // @ts-ignore
                                                                store={
                                                                    store.name
                                                                }
                                                            >
                                                                {store.name}
                                                            </option>
                                                        )
                                                    )}
                                            </TextField>
                                        </Box>
                                    </Box>
                                </Box>
                                {error && (
                                    <AlertP
                                        severity="error"
                                        msg={error.error}
                                    />
                                )}

                                {errors.submit && (
                                    <Box mt={3}>
                                        <FormHelperText error>
                                            {errors.submit}
                                        </FormHelperText>
                                    </Box>
                                )}
                            </CardContent>
                            <Divider />
                            <Box p={2}>
                                <CardActions>
                                    <Box flexGrow={1} />
                                    <Button
                                        onClick={onCancel}
                                        variant="contained"
                                        type="submit"
                                        color="secondary"
                                        className={classes.cancelButton}
                                    >
                                        {t('Buttons.Cancel')}
                                    </Button>
                                    <Button
                                        color="primary"
                                        disabled={isSubmitting}
                                        variant="contained"
                                        type="submit"
                                    >
                                        {t('Buttons.Add')}
                                    </Button>
                                </CardActions>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Card>
        );
    }
);
